import { Fragment, useEffect, useState } from 'react'
import {
  Root,
  TopBar,
  Container,
  Burger,
  Logo,
  Searchbar,
  Actions,
  Navigation,
  Skeleton
} from '@leroy-merlin-br/backpack-header'
import type {
  UserResources,
  UserContext
} from '@leroy-merlin-br/backpack-services'
import { useGetApiV3CategoriesTree } from '@leroy-merlin-br/backpack-services'

import emitter from 'scripts/utils/emitter'

import { EmployeeNotification } from 'shared/components'
import { useLoyaltyProfile, useUserResources } from 'shared/hooks'
import {
  insightsConfig,
  searchClient,
  indexName
} from 'shared/contexts/AlgoliaInstantSearch/lib'

import { useCartDrawerContext } from '../Header/contexts/CartDrawerContext'
import { handleUserRecontext } from './utils/handleUserRecontext'

export interface NewHeaderProps {
  /**
   * Feature flip to black-friday
   */
  isBlackWeekend: boolean

  /**
   * Feature flip to enable/disable social provider
   */
  socialLoginProviders: Array<'google' | 'facebook' | 'apple'>
}

function NewHeader (props: NewHeaderProps) {
  const { isBlackWeekend = false, socialLoginProviders } = props

  const { userResources } = useUserResources()
  const { loyaltyProfile, isLoading: isLoadingLoyalty } = useLoyaltyProfile()
  const { toggleCartDrawer } = useCartDrawerContext()
  const { data: categories } = useGetApiV3CategoriesTree()

  const [cartItems, setCartItems] = useState<UserResources['cartItems']>(null)
  const [userContext, setUserContext] = useState<UserContext>()

  const theme = isBlackWeekend ? 'black-weekend' : 'default'

  useEffect(() => {
    setCartItems(userResources?.cartItems || null)

    if (userResources?.region) {
      setUserContext({
        region: userResources.region,
        regionName: userResources.selectedRegionName,
        locationSource: userResources.locationSource || 'user'
      })
    }
  }, [userResources])

  useEffect(() => {
    emitter.on('cart:update', ({ count }) => setCartItems(count || null))
    emitter.on('cart:region-changed', data => {
      setUserContext(data)
    })
  }, [])

  const shouldShowShell = !userResources || !categories || isLoadingLoyalty

  if (shouldShowShell) {
    return <Skeleton theme={theme} />
  }

  return (
    <Fragment>
      <Root
        user={{
          ...userResources,
          ...userContext
        }}
        loyalty={loyaltyProfile}
        session={{
          cartItems: Number(cartItems)
        }}
        theme={theme}
      >
        <TopBar handleLocation={handleUserRecontext} />
        <Container>
          <Burger />
          <Logo />
          <Searchbar
            algolia={{
              insightsClient: insightsConfig.insightsClient,
              searchClient,
              indexName
            }}
          />
          <Actions
            onClickCart={toggleCartDrawer}
            providers={{
              disable: socialLoginProviders
            }}
          />
        </Container>

        <Navigation data={categories.data.results} />
      </Root>

      <EmployeeNotification />
    </Fragment>
  )
}

export default NewHeader
